import type { Snapshot } from '../../types';

export type Stage =
  | 'pending'
  | 'connecting'
  | 'transferring'
  | 'confirming'
  | 'completed'
  | 'paused'
  | 'failed'
  | 'cancelled';
export type Reason =
  | 'connection'
  | 'protocol'
  | 'corrupt'
  | 'association'
  | 'time'
  | 'capacity'
  | 'confirmation';
export interface Association {
  title: string;
  campus: string;
  teacherMemberUuid: string;
  lessonUuid?: string;
  scene: 'classroom' | 'communication' | 'other';
  startedAt: string;
  confirmed: boolean;
}
export interface ImportFile {
  uuid: string;
  filename: string;
  sizeBytes: number;
  durationMs: number;
  sha256: string;
  blob?: Blob;
  error?: string;
  lastModified: number;
  association: Association;
  status: 'invalid' | 'ready' | 'confirmed' | 'duplicate' | 'failed';
  sourceFileUuid?: string;
  recordingUuid?: string;
}
export interface SourceFile {
  uuid: string;
  batchUuid: string;
  queueItemUuid: string;
  sourceFileKey: string;
  deviceUuid?: string;
  filename: string;
  sizeBytes: number;
  durationMs: number;
  sha256: string;
  blob?: Blob;
  fixture?: number;
  integrity: 'pending' | 'valid' | 'corrupt' | 'duplicate';
  stage: Stage;
  previousStage?: Stage;
  transferredBytes: number;
  confirmedAt?: string;
  uploadedAt?: string;
  recordingUuid?: string;
  duplicateOfFileUuid?: string;
  association: Association;
  bindingSnapshot?: {
    uuid: string;
    memberUuid: string;
    campus: string;
    effectiveFrom: string;
    effectiveTo?: string;
  };
  reason?: Reason;
  failure?: string;
  retries: number;
  failOnce?: Reason;
}
export interface QueueItem {
  uuid: string;
  batchUuid: string;
  deviceUuid: string;
  deviceName: string;
  method: 'usb' | 'network';
  capability: string;
  fileUuids: string[];
  generation: number;
}
export interface Batch {
  uuid: string;
  title: string;
  stationUuid: string;
  campus: string;
  itemUuids: string[];
  createdAt: string;
  revision: number;
  legacySummary?: { status: string; progress: number; note: string };
}
export interface StationConfig {
  uuid: string;
  name: string;
  campus: string;
  enabled: boolean;
  online: boolean;
  methods: ('usb' | 'network')[];
  capacityBytes: number;
  revision: number;
}
export interface CollectionEvent {
  uuid: string;
  batchUuid?: string;
  fileUuid?: string;
  at: string;
  text: string;
  actorMemberUuid: string;
}
export interface ImportPreview {
  uuid: string;
  expiresAt: number;
  fileUuids: string[];
  revision: number;
  contextKey: string;
}
export interface CollectionData {
  version: 1;
  revision: number;
  stations: StationConfig[];
  batches: Batch[];
  items: QueueItem[];
  files: SourceFile[];
  imports: ImportFile[];
  events: CollectionEvent[];
  receipts: Record<string, string>;
  lastTick: number;
  preview?: ImportPreview;
}
export type CollectionSnapshot = Snapshot & { collectionV1?: CollectionData };
export interface RecordingImportDraft extends Association {
  sourceFileUuid: string;
  batchUuid: string;
  sha256: string;
  filename: string;
  sizeBytes: number;
  durationMs: number;
  blob?: Blob;
  deviceUuid?: string;
  bindingSnapshot?: SourceFile['bindingSnapshot'];
}
export interface RecordingSourceConfirmed {
  sourceFileUuid: string;
  recordingUuid: string;
  confirmedAt: string;
  result: 'created' | 'duplicate';
  duplicateOfFileUuid?: string;
}
export type CollectionCommand =
  | { type: 'initialize' }
  | {
      type: 'legacyControl';
      batchUuid: string;
      action: 'pause' | 'resume' | 'retry' | 'cancel';
    }
  | {
      type: 'enqueue';
      stationUuid: string;
      deviceUuids: string[];
      method: 'usb' | 'network';
      interruptSecond?: boolean;
      samples: ImportFile[];
    }
  | {
      type: 'control';
      fileUuids: string[];
      action: 'pause' | 'resume' | 'retry' | 'cancel';
    }
  | { type: 'reorder'; itemUuid: string; direction: -1 | 1 }
  | { type: 'station'; station: StationConfig }
  | { type: 'reassign'; itemUuid: string; stationUuid: string }
  | { type: 'saveImports'; files: ImportFile[] }
  | { type: 'replaceImport'; fileUuid: string; file: ImportFile }
  | { type: 'associate'; fileUuid: string; association: Association }
  | { type: 'removeImport'; fileUuid: string }
  | { type: 'previewImport'; fileUuids: string[] }
  | { type: 'confirmImport'; previewUuid: string }
  | {
      type: 'resolve';
      fileUuid: string;
      action:
        | 'retry'
        | 'association'
        | 'confirmAssociation'
        | 'duplicate'
        | 'ticket';
      note: string;
      association?: Association;
    }
  | { type: 'tick'; at: number };
export interface CollectionMeta {
  commandUuid: string;
  expectedRevision?: number;
}
export const stageLabels: Record<Stage | 'partial', string> = {
  pending: '待开始',
  connecting: '连接中',
  transferring: '传输中',
  confirming: '待后台确认',
  completed: '已确认',
  paused: '已暂停',
  failed: '失败',
  cancelled: '已取消',
  partial: '部分成功',
};
export const reasonLabels: Record<Reason, string> = {
  connection: '连接中断',
  protocol: '协议与能力',
  corrupt: '文件损坏',
  association: '归属缺失',
  time: '发生时间异常',
  capacity: '容量不足',
  confirmation: '后台确认失败',
};
export const activeStages: Stage[] = [
  'pending',
  'connecting',
  'transferring',
  'confirming',
];
export const unfinished = (f: SourceFile) =>
  !['completed', 'cancelled'].includes(f.stage);
export function batchFiles(data: CollectionData, batchUuid: string) {
  return data.files.filter((f) => f.batchUuid === batchUuid);
}
export function batchStatus(files: SourceFile[]): Stage | 'partial' {
  if (!files.length) return 'pending';
  if (files.every((f) => f.stage === 'completed')) return 'completed';
  const active = files.find((f) => activeStages.includes(f.stage));
  if (active) return active.stage;
  if (files.some((f) => f.stage === 'failed'))
    return files.some((f) => f.stage === 'completed') ? 'partial' : 'failed';
  if (files.some((f) => f.stage === 'paused')) return 'paused';
  return 'cancelled';
}
export function stationBytes(data: CollectionData, stationUuid: string) {
  const ids = new Set(
    data.batches
      .filter((b) => b.stationUuid === stationUuid)
      .map((b) => b.uuid),
  );
  return data.files
    .filter((f) => ids.has(f.batchUuid) && unfinished(f))
    .reduce((sum, f) => sum + f.sizeBytes, 0);
}
export function visibleCampus(campus: string, state: Snapshot) {
  return state.context.campus === '全部校区' || state.context.campus === campus;
}
